// Case software updates, for the Software tab: what's available, whether the
// case can take it now, and how an update in progress or finished reads.
//
// The case's updater (sitcase-updater) reports state/update.json through the
// `update-status` dashboard command:
//   { id, action: update|rollback, target, from, version, phase, attempt,
//     result: updated|rolled_back|failed|refused, error, running, ... }

import { baseVersionOf, compareVersions } from "utils/firmwareRelease";

/** The stepper shown while a case updates. */
export const UPDATE_STEPS = [
  "sending",
  "checking",
  "backingUp",
  "installing",
  "starting",
  "healthCheck",
];

const PHASE_STEP = {
  uploading: 0,
  verifying: 1,
  preflight: 1,
  backing_up: 2,
  installing: 3,
  switching: 4,
  health_check: 5,
  retrying: 5,
};

const ACTIVE_PHASES = new Set([
  "verifying",
  "preflight",
  "backing_up",
  "installing",
  "switching",
  "health_check",
  "retrying",
  "rolling_back",
]);

/** Index into UPDATE_STEPS for an updater phase (null when not a step). */
export const stepOf = (phase) =>
  Object.prototype.hasOwnProperty.call(PHASE_STEP, phase)
    ? PHASE_STEP[phase]
    : null;

/** An update or rollback is running on the case now. */
export const isUpdating = (state) =>
  !!state && (state.running === true || state.phase === "uploading");

/** Cut off by a restart; the case finishes or undoes it at its next boot. */
export const isInterrupted = (state) =>
  !!state && !state.running && ACTIVE_PHASES.has(state.phase);

/**
 * How a release from the API relates to what the case runs:
 * "same" (that exact build), "newer" / "older" (by internal version),
 * "different" (same internal version, another build), or "unknown".
 */
export function compareRelease(release, current) {
  if (!release) return "unknown";
  if (current?.label && release.tag === current.label) return "same";
  const theirs = release.base_version || baseVersionOf(release.version);
  const ours = current?.baseVersion || baseVersionOf(current?.version);
  const cmp = compareVersions(theirs, ours);
  if (cmp === null) return "unknown";
  if (cmp > 0) return "newer";
  if (cmp < 0) return "older";
  return "different";
}

/**
 * Why the case can't take an update right now, or null. `health` is the
 * `health` command's snapshot, `versions` the `version` command's result.
 */
export function updateBlockedReason({ health, versions, updateState }) {
  if (versions && versions.layout === "checkout") return "checkout";
  if (isUpdating(updateState)) return "updating";
  const fresh = health && !health.stale && health.status !== "unknown";
  if (fresh && health.session_active) return "session";
  if (fresh && health.harvest_active) return "harvest";
  return null;
}

/** The finished result of the last run, or null while one runs / never ran. */
export function outcomeOf(state) {
  if (!state || state.phase !== "done" || !state.result) return null;
  return {
    kind: state.result,
    action: state.action,
    version: state.version || state.target,
    from: state.from,
    error: state.error || null,
    firstFailure: state.first_failure || null,
    finishedAt: state.finished_at || state.updated_at || null,
  };
}

/** 207_000_000 → "197 MB". */
export function fmtBytes(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${Math.round(v / 1024)} KB`;
  if (v < 1024 * 1024 * 1024) return `${Math.round(v / 1024 / 1024)} MB`;
  return `${(v / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

/** Plain text for "Copy details": everything a bug report needs. */
export function updateReport({ caseName, state, lines = [] }) {
  const out = [
    `Case: ${caseName || "?"}`,
    `Action: ${state?.action || "?"} ${state?.target || ""}`.trim(),
    `From: ${state?.from || "—"}`,
    `Result: ${state?.result || state?.phase || "?"}`,
  ];
  if (state?.error) out.push(`Error: ${state.error}`);
  if (state?.first_failure) out.push(`First failure: ${state.first_failure}`);
  if (state?.started_at) out.push(`Started: ${state.started_at}`);
  if (state?.finished_at) out.push(`Finished: ${state.finished_at}`);
  const tail = lines.filter(Boolean).slice(-50);
  if (tail.length) out.push("", "Last log lines:", ...tail);
  return out.join("\n");
}
